import { DateOfBirthField } from './date-of-birth-field'
import { GenderField } from './gender-field'
import { HeightField } from './height-field'
import {
  UpdatePersonalDetailsFormData,
  updatePersonalDetailsValidationSchema,
} from './update-personal-details-form-validation-schema'
import { WeightField } from './weight-field'
import { yupResolver } from '@hookform/resolvers/yup'
import React, { useCallback, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { OutlinedButton } from 'src/component/ui/button'
import { Field } from 'src/component/ui/field'
import { useSessionContext } from 'src/context/session-context'
import { HeightUnit, WeightUnit } from 'src/dao/core-dao'
import { useUpdatePersonalDetails } from 'src/hook/use-update-personal-details'
import { timeUtil } from 'src/util/time-util'

interface EditPersonalDetailsProps {
  onSave: () => void
  onCancel: () => void
}

export const EditPersonalDetails = ({ onSave, onCancel }: EditPersonalDetailsProps): React.ReactElement => {
  const { me } = useSessionContext()

  const defaultPersonalDetailsFormValues = useMemo(
    () => ({
      username: me?.profile?.username || '',
      gender: me?.profile?.gender || undefined,
      dateOfBirth: me?.profile?.dateOfBirth ? timeUtil.formatInDate(new Date(me.profile.dateOfBirth)) : '',
      heightValue: me?.profile?.height?.value ? String(me.profile.height.value) : '',
      heightUnit: (me?.profile?.height?.unit as HeightUnit) || HeightUnit.CM,
      weightValue: me?.profile?.weight?.value ? String(me.profile.weight.value) : '',
      weightUnit: (me?.profile?.weight?.unit as WeightUnit) || WeightUnit.KG,
    }),
    [me?.profile],
  )

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isValid },
  } = useForm<UpdatePersonalDetailsFormData>({
    defaultValues: defaultPersonalDetailsFormValues,
    mode: 'onTouched',
    resolver: yupResolver(updatePersonalDetailsValidationSchema),
  })

  const { updatePersonalDetails, isUpdatingPersonalDetails } = useUpdatePersonalDetails({
    onSuccess: () => {
      onSave()
    },
    setError,
  })

  const handleSave = useCallback(
    (formData: UpdatePersonalDetailsFormData): void => {
      updatePersonalDetails(formData)
    },
    [updatePersonalDetails],
  )

  return (
    <>
      <div className="flex items-center justify-between !mb-5">
        <h6 className="font-evogria text-sm md:text-base text-ivory uppercase">Personal Details</h6>
        <div className="flex items-center gap-4">
          <OutlinedButton size="sm" className="border-ivory text-ivory" onClick={onCancel}>
            Cancel
          </OutlinedButton>
          <OutlinedButton
            size="sm"
            onClick={handleSubmit(handleSave)}
            disabled={!isValid || isUpdatingPersonalDetails}
            className="text-primary"
            isLoading={isUpdatingPersonalDetails}
          >
            Save
          </OutlinedButton>
        </div>
      </div>
      <form onSubmit={handleSubmit(handleSave)} className="space-y-4">
        <Controller
          control={control}
          name="username"
          render={({ field: { onChange, value, onBlur } }) => (
            <Field
              size="sm"
              label="Username"
              name="username"
              value={value}
              placeholder="Enter username"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
              onBlur={onBlur}
              error={errors.username?.message}
              disabled={isUpdatingPersonalDetails}
            />
          )}
        />
        <Controller
          control={control}
          name="gender"
          render={({ field: { onChange, value } }) => (
            <GenderField
              value={value ?? undefined}
              onChange={onChange}
              error={errors.gender?.message}
              disabled={isUpdatingPersonalDetails}
            />
          )}
        />
        <Controller
          control={control}
          name="dateOfBirth"
          render={({ field: { onChange, value } }) => (
            <DateOfBirthField
              value={value ?? undefined}
              onChange={onChange}
              error={errors.dateOfBirth?.message}
              disabled={isUpdatingPersonalDetails}
              placeholder="Select date of birth"
            />
          )}
        />
        <div className="flex items-top gap-4">
          <Controller
            control={control}
            name="heightValue"
            render={({ field: { onChange: onValueChange, value: heightValue } }) => (
              <Controller
                control={control}
                name="heightUnit"
                render={({ field: { onChange: onUnitChange, value: heightUnit } }) => (
                  <HeightField
                    value={heightValue || ''}
                    unit={heightUnit as HeightUnit}
                    onValueChange={onValueChange}
                    onUnitChange={onUnitChange}
                    error={errors.heightValue?.message}
                    disabled={isUpdatingPersonalDetails}
                  />
                )}
              />
            )}
          />
          <Controller
            control={control}
            name="weightValue"
            render={({ field: { onChange: onValueChange, value: weightValue } }) => (
              <Controller
                control={control}
                name="weightUnit"
                render={({ field: { onChange: onUnitChange, value: weightUnit } }) => (
                  <WeightField
                    value={weightValue || ''}
                    unit={weightUnit as WeightUnit}
                    onValueChange={onValueChange}
                    onUnitChange={onUnitChange}
                    error={errors.weightValue?.message}
                    disabled={isUpdatingPersonalDetails}
                  />
                )}
              />
            )}
          />
        </div>
      </form>
    </>
  )
}
